import { useState, useEffect } from "react";
import { MapPin, ExternalLink, Phone, Clock, Loader2, Store } from "lucide-react";
import { useGeolocation } from "@/hooks/use-geolocation";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Bookstore {
  name: string;
  address: string | null;
  phone: string | null;
  website: string | null;
  opening_hours: string | null;
  latitude: number;
  longitude: number;
  distance: number;
}

export default function NearbyBookstores() {
  const { latitude, longitude, loading: geoLoading, error: geoError, permitted, requestLocation } =
    useGeolocation();
  const [bookstores, setBookstores] = useState<Bookstore[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!latitude || !longitude) return;

    const fetchBookstores = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/nearby-bookstores`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            },
            body: JSON.stringify({ latitude, longitude }),
          }
        );
        if (!resp.ok) throw new Error("Failed to fetch bookstores");
        const data = await resp.json();
        setBookstores(data.bookstores || []);
      } catch {
        setError("Couldn't find bookstores nearby. Try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchBookstores();
  }, [latitude, longitude]);

  const mapsUrl = (lat: number, lon: number, name: string) =>
    `https://www.google.com/maps/search/${encodeURIComponent(name)}/@${lat},${lon},17z`;

  // Not yet asked for location
  if (!permitted && !geoLoading && !geoError) {
    return (
      <div className="rounded-2xl border border-border/50 bg-card p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Store size={18} />
          </div>
          <h3 className="font-display text-base font-bold text-foreground">
            Support Local Bookstores
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Find indie bookstores near you to grab your next read in person.
        </p>
        <Button
          onClick={requestLocation}
          variant="outline"
          size="sm"
          className="rounded-xl gap-2"
        >
          <MapPin size={14} />
          Find Bookstores Near Me
        </Button>
      </div>
    );
  }

  // Loading states
  if (geoLoading || loading) {
    return (
      <div className="rounded-2xl border border-border/50 bg-card p-5">
        <div className="flex items-center gap-3">
          <Loader2 size={18} className="animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">
            {geoLoading ? "Getting your location…" : "Searching for nearby bookstores…"}
          </span>
        </div>
      </div>
    );
  }

  // Error
  if (geoError || error) {
    return (
      <div className="rounded-2xl border border-border/50 bg-card p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
            <MapPin size={18} />
          </div>
          <h3 className="font-display text-base font-bold text-foreground">
            Bookstore Finder
          </h3>
        </div>
        <p className="text-sm text-muted-foreground">{geoError || error}</p>
        {!geoError && (
          <Button onClick={requestLocation} variant="outline" size="sm" className="mt-3 rounded-xl gap-2">
            <MapPin size={14} />
            Try Again
          </Button>
        )}
      </div>
    );
  }

  // No results
  if (bookstores.length === 0) {
    return (
      <div className="rounded-2xl border border-border/50 bg-card p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Store size={18} />
          </div>
          <h3 className="font-display text-base font-bold text-foreground">
            No Bookstores Found Nearby
          </h3>
        </div>
        <p className="text-sm text-muted-foreground">
          We couldn't find any bookstores within 8 km. Try{" "}
          <a
            href="https://bookshop.org"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-2 hover:text-primary/80"
          >
            Bookshop.org
          </a>{" "}
          — they share profits with local indie bookstores!
        </p>
      </div>
    );
  }

  const displayed = expanded ? bookstores : bookstores.slice(0, 3);

  return (
    <div className="rounded-2xl border border-border/50 bg-card p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Store size={18} />
        </div>
        <div>
          <h3 className="font-display text-base font-bold text-foreground">
            Bookstores Near You
          </h3>
          <p className="text-xs text-muted-foreground">
            Support indie — buy local 🧡
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {displayed.map((store, i) => (
          <div
            key={i}
            className="rounded-xl border border-border/30 bg-background/50 p-3 transition-colors hover:bg-muted/50"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-sm text-foreground truncate">{store.name}</p>
                {store.address && (
                  <p className="text-xs text-muted-foreground mt-0.5">{store.address}</p>
                )}
                <p className="text-xs text-muted-foreground mt-0.5">
                  {store.distance < 1
                    ? `${Math.round(store.distance * 1000)} m away`
                    : `${store.distance.toFixed(1)} km away`}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {store.phone && (
                  <a
                    href={`tel:${store.phone}`}
                    className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    title="Call"
                  >
                    <Phone size={14} />
                  </a>
                )}
                {store.website && (
                  <a
                    href={store.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    title="Website"
                  >
                    <ExternalLink size={14} />
                  </a>
                )}
                <a
                  href={mapsUrl(store.latitude, store.longitude, store.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg p-1.5 text-primary hover:bg-primary/10 transition-colors"
                  title="Open in Maps"
                >
                  <MapPin size={14} />
                </a>
              </div>
            </div>
            {store.opening_hours && (
              <div className="flex items-center gap-1 mt-1.5 text-xs text-muted-foreground">
                <Clock size={11} />
                <span>{store.opening_hours}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {bookstores.length > 3 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
        >
          {expanded ? "Show fewer" : `Show all ${bookstores.length} bookstores`}
        </button>
      )}

      <div className="mt-4 pt-3 border-t border-border/30">
        <p className="text-xs text-muted-foreground">
          Prefer online?{" "}
          <a
            href="https://bookshop.org"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-2 hover:text-primary/80 font-medium"
          >
            Bookshop.org
          </a>{" "}
          shares profits with local indie bookstores.
        </p>
      </div>
    </div>
  );
}
